import React, { useEffect, Suspense } from "react";
import { NavLink } from 'react-router-dom';
import { Helmet } from "react-helmet";
import mixpanel from "mixpanel-browser";
import "./TransferPageTemplate.scss";

import ReviewsComponent from "../../components/ReviewsComponent";
import ServicesComponent from "../../components/ServicesComponent";
import TripInfoComponent from "../../components/TripInfoComponent";
import ImpactStatsComponent from "../../components/ImpactStatsComponent";
import WhatsAppCtaComponent from "../../components/WhatsAppCtaComponent";
import HourlyRentalInquiryComponent from "../../components/HourlyRentalInquiryComponent";

// Lazy load components
const OurFleetComponent = React.lazy(() => import("../../components/OurFleetComponent"));
const PickupServiceSteps = React.lazy(() => import("../../components/PickupServiceSteps"));
const SearchSectionComponent = React.lazy(() => import("../../components/SearchSectionComponent"));
const LoaderComponent = React.lazy(() => import("../../components/LoaderComponent"));
const BudapestActivitiesComponent = React.lazy(() => import("../../components/BudapestActivitiesComponent"));

const TransferPageTemplate = ({ transferData }) => {
  useEffect(() => {
    mixpanel.track(`Visited ${transferData.title} Page`);
  }, [transferData.title]);

  return (
    <div className="transfer-page">
      <Helmet>
        <title>{transferData.pageTitle}</title>
        <meta
          name="description"
          content={transferData.pageDescription}
        />
        <meta
          name="keywords"
          content={transferData.pageKeywords}
        />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://pickuphungary.com/${transferData.id}`} />
        
        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content={transferData.pageTitle} />
        <meta property="og:description" content={transferData.pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://pickuphungary.com/${transferData.id}`} />
        <meta property="og:image" content="https://pickuphungary.com/images/mercedes-driver-open-door.jpg" />
        <meta property="og:site_name" content="Pickup Hungary" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={transferData.pageTitle} />
        <meta name="twitter:description" content={transferData.pageDescription} />
        <meta name="twitter:image" content="https://pickuphungary.com/images/mercedes-driver-open-door.jpg" />
        
        {/* Additional SEO meta tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Pickup Hungary" />
        <meta name="geo.region" content="HU" />
        <meta name="geo.placename" content={transferData.destinationFrom} />
        
        {/* Structured data for LocalBusiness and Service */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "${transferData.title}",
              "description": "${transferData.pageDescription}",
              "provider": {
                "@type": "LocalBusiness",
                "name": "Pickup Hungary",
                "image": "https://pickuphungary.com/images/mercedes-driver-open-door.jpg",
                "address": {
                  "@type": "PostalAddress",
                  "addressCountry": "Hungary"
                },
                "priceRange": "${transferData.quickInfo.find(info => info.icon === "fa-euro-sign")?.text || ""}",
                "telephone": "+36707177656"
              },
              "serviceType": "Private Transfer",
              "areaServed": {
                "@type": "City",
                "name": "${transferData.destinationFrom}"
              },
              "serviceOutput": "Transportation from ${transferData.destinationFrom} to ${transferData.destinationTo || 'your destination'}"
            }
          `}
        </script>
      </Helmet>

      <section className="transfer-page__hero" aria-label="Search for transfers">
        <Suspense fallback={<LoaderComponent />}>
          <SearchSectionComponent 
            title={transferData.title}
            fromLat={transferData.fromLat}
            fromLng={transferData.fromLng}
            destinationFrom={transferData.destinationFrom}
            toLat={transferData.toLat}
            toLng={transferData.toLng}
            destinationTo={transferData.destinationTo}
            showInfo={transferData.showInfo}
          />
        </Suspense>
      </section>

      <main className="transfer-page__content">
        <div className="container">
          <div className="row align-items-center mb-4">
            <div className="col-lg-8">
              <header className="transfer-page__header">
                <h1>{transferData.headerTitle}</h1>
                <h2>{transferData.headerSubtitle}</h2>
                <p className="lead">{transferData.headerDescription}</p>
                {transferData.showHourlyRental !== false && (
                <div className="hourly-rental-cta">
                  <p>Looking for hourly rental instead, with the same drop-off as your starting point? Starting from 20€/hour</p>
                  <a href="#hourly-rental" className="btn btn-outline-primary">
                    <i className="fa-solid fa-arrow-down"></i> Hourly Rental
                  </a>
                </div>
              )}
              </header>
            </div>
            <div className="col-lg-4">
              <WhatsAppCtaComponent 
                phoneNumber="36707177656"
                title="Need More Information?"
                description="Our travel concierge is ready to assist you with any questions about your journey or to help customize your trip."
              />
            </div>
          </div>

          <div className="row mt-5">
            <div className="col-12">
              <section className="transfer-page__journey-highlights" aria-label="Journey highlights">
                <div className="journey-highlight-item">
                  <div className="highlight-icon">
                    <i className="fa-solid fa-clock"></i>
                  </div>
                  <div className="highlight-content">
                    <h4>Duration</h4>
                    <p>{transferData.quickInfo.find(info => info.icon === "fa-clock")?.text || ""}</p>
                  </div>
                </div>
                
                <div className="journey-highlight-item">
                  <div className="highlight-icon">
                    <i className="fa-solid fa-road"></i>
                  </div>
                  <div className="highlight-content">
                    <h4>Distance</h4>
                    <p>{transferData.quickInfo.find(info => info.icon === "fa-car")?.text || ""}</p>
                  </div>
                </div>
                
                <div className="journey-highlight-item">
                  <div className="highlight-icon">
                    <i className="fa-solid fa-euro-sign"></i>
                  </div>
                  <div className="highlight-content">
                    <h4>Price</h4>
                    <p>{transferData.quickInfo.find(info => info.icon === "fa-euro-sign")?.text || ""}</p>
                  </div>
                </div>
                
                {transferData.showHourlyRental !== false && (
                  <div className="journey-highlight-item hourly-highlight">
                    <div className="highlight-icon">
                      <i className="fa-solid fa-clock-rotate-left"></i>
                    </div>
                    <div className="highlight-content">
                      <h4>Hourly Rental</h4>
                      <p>20€/hour</p>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>

          <article>
            <Suspense fallback={<LoaderComponent />}>
              <TripInfoComponent transferData={transferData} />
            </Suspense>
          </article>

          <section aria-label="Additional information">
            <Suspense fallback={<LoaderComponent />}>
              <ImpactStatsComponent />
              <ServicesComponent />
              <PickupServiceSteps />
            </Suspense>
          </section>

        {/* Alternative transport options are now shown in the TripInfoComponent */}

        {/* Hourly Rental Inquiry Section */}
        {transferData.showHourlyRental !== false && (
          <section id="hourly-rental" aria-label="Hourly rental service">
            <HourlyRentalInquiryComponent transferTitle={transferData.title} />
          </section>
        )}

        {transferData.showBudapestActivities && (
          <section aria-label="Budapest activities">
            <Suspense fallback={<LoaderComponent />}>
              <BudapestActivitiesComponent />
            </Suspense>
          </section>
        )}

        <section aria-label="Customer reviews" className="mt-4">
          <ReviewsComponent />
        </section>
      </main>

      <div className="container mb-5">
        <footer className="transfer-page__cta">
          <p>{transferData.ctaText}</p>
          <NavLink to="/booking" className="btn btn-lg btn-primary" aria-label="Book your transfer now">Book Now</NavLink>
        </footer>
      </div>
    </div>
  );
};

export default TransferPageTemplate;
